import Notify from 'notifyjs'
import { observable, runInAction, makeObservable } from 'mobx'
import { localStorage as storage } from 'infra/storage'
import { stripMarkdown } from 'utils/markdown-util'
import { Observable, NEVER } from 'rxjs'
import User from 'iam/models/User'
import { SystemNotification } from 'types/definitions'

const DEFAULT_ICON = require('assets/images/taxfyle-icon-256px.svg')

export default class DesktopNotificationStore {
  @observable
  isSupported = false

  @observable
  isEnabled = false

  @observable
  isDenied = false

  constructor(currentUser$: Observable<User>) {
    makeObservable(this)
    this.isSupported = !Notify.needsPermission || Notify.isSupported()

    if (this.isSupported) {
      this.isDenied = Notification.permission === 'denied'
    }

    currentUser$.subscribe(() => {
      // Restore enabled state.
      const stored = storage.getItem('desktopNotifications')
      const enabled = stored !== 'disabled'
      this.setEnabled(enabled, stored !== null).then()
    })
  }

  async setEnabled(enabled: boolean, noShowWelcome = false) {
    if (enabled) {
      if (this.isEnabled && !Notify.needsPermission) {
        return
      }
      enabled = await this._getPermission()
      runInAction(() => {
        this.isDenied = !enabled
      })
    }

    storage.setItem('desktopNotifications', enabled ? 'enabled' : 'disabled')
    runInAction(() => {
      this.isEnabled = enabled
    })

    if (this.isEnabled && !noShowWelcome) {
      this.show({
        title: 'Nice!',
        body: 'Desktop notifications have been enabled. You rock!',
        tag: 'notifications-enabled',
      })
    }
  }

  _getPermission(): Promise<boolean> {
    return new Promise((resolve) => {
      if (!Notify.needsPermission) {
        return resolve(true)
      }

      if (!this.isSupported) {
        return resolve(false)
      }

      Notify.requestPermission(
        () => resolve(true),
        () => resolve(false),
      )
    })
  }

  show = (opts: SystemNotification) => {
    if (!this.isEnabled) {
      return
    }

    const closeSubscription = (opts.close$ ?? NEVER).subscribe(() => {
      notification?.close()
    })

    const notification = new Notify(opts.title, {
      tag: opts.tag,
      body: stripMarkdown(opts.body),
      icon: opts.icon || DEFAULT_ICON,
      timeout: opts.timeout || 5,
      notifyError: () => {
        closeSubscription?.unsubscribe()
      },
      notifyClick: () => {
        window.focus()
        if (opts.onClick) {
          opts.onClick()
        }
        notification.close()
      },
      notifyClose: () => {
        closeSubscription?.unsubscribe()
      },
    })

    notification.show()
  }
}
